// CLI project helpers (ARCHITECTURE.md §14).
//
// Bridges the agent-contracts DSL and the analyzer pipeline for the CLI:
// resolves the DSL to extract artifact `path_patterns` and a Navigation Index,
// then runs the AST → Dependency Graph stages. DSL resolution is best-effort —
// a project without contracts still yields a structural graph (with an empty
// artifact mapping) rather than aborting.

import { stat, unlink, writeFile } from "node:fs/promises";
import { basename, dirname, join } from "node:path";

import { resolve as resolveContracts, buildNavigationIndex } from "agent-contracts";
import {
  loadDocument,
  resolveDocument,
  type ResolvedArtifact,
  type TraceLink,
} from "artifact-contracts/core";

import { buildAstGraph } from "../ast/index.js";
import { buildDependencyGraph } from "../dependency/index.js";
import type { DependencyGraph } from "../dependency/types.js";
import { createDefaultRegistry } from "../analyzer/index.js";
import { integrateExternalInsights } from "../external/index.js";
import type {
  InsightProvider,
  ProviderError,
  UnresolvedEdge,
} from "../external/types.js";
import { loadCandidateEdges, openStore } from "../store/index.js";
import type { StoreHandle } from "../store/types.js";

/** Raised for recoverable analysis failures; mapped to CLI exit code 1. */
export class AnalysisError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AnalysisError";
  }
}

export interface ProjectContext {
  /** artifactId → glob patterns from the DSL (empty when no DSL resolves). */
  artifactPatterns: Map<string, string[]>;
  /** agent-contracts Navigation Index, when a DSL resolved. */
  navigationIndex?: Record<string, unknown>;
  /** Resolved artifact definitions from artifact-contracts.yaml, when available. */
  artifactDefinitions?: Record<string, ResolvedArtifact>;
  /** Trace links from artifact-contracts.yaml, when available. */
  traceLinks?: TraceLink[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Extract artifactId → path_patterns from either a Navigation Index
 * (`artifacts[id].files.path_patterns`) or a raw DSL (`artifacts[id]
 * .path_patterns`). Non-string patterns and pattern-less artifacts are dropped.
 */
function extractArtifactPatterns(
  source: Record<string, unknown> | undefined,
): Map<string, string[]> {
  const map = new Map<string, string[]>();
  const artifacts = source?.["artifacts"];
  if (!isRecord(artifacts)) return map;

  for (const [id, node] of Object.entries(artifacts)) {
    if (!isRecord(node)) continue;

    let raw: unknown;
    const files = node["files"];
    if (isRecord(files) && Array.isArray(files["path_patterns"])) {
      raw = files["path_patterns"];
    } else if (Array.isArray(node["path_patterns"])) {
      raw = node["path_patterns"];
    }

    const patterns = Array.isArray(raw)
      ? raw.filter((p): p is string => typeof p === "string")
      : [];
    if (patterns.length > 0) map.set(id, patterns);
  }
  return map;
}

interface ArtifactContractsLoadResult {
  artifactPatterns: Map<string, string[]>;
  artifactDefinitions: Record<string, ResolvedArtifact>;
  traceLinks: TraceLink[];
}

function readPathPatterns(artifact: ResolvedArtifact): string[] {
  const raw = (artifact as { path_patterns?: unknown }).path_patterns;
  return Array.isArray(raw)
    ? raw.filter((p): p is string => typeof p === "string")
    : [];
}

/**
 * Load artifact-contracts.yaml when present. Returns null when the file is
 * absent or fails to parse — callers fall back to agent-contracts.
 */
async function tryLoadArtifactContracts(
  projectRoot: string,
  artifactContractsPath?: string,
): Promise<ArtifactContractsLoadResult | null> {
  const yamlPath =
    artifactContractsPath ?? join(projectRoot, "artifact-contracts.yaml");

  try {
    const info = await stat(yamlPath);
    if (!info.isFile()) return null;
  } catch {
    return null;
  }

  const configDir = dirname(yamlPath);
  const yamlBasename = basename(yamlPath);
  const previousCwd = process.cwd();
  let tempConfigPath: string | undefined;

  try {
    process.chdir(configDir);

    let loadConfigPath: string | undefined;
    if (yamlBasename !== "artifact-contracts.yaml") {
      tempConfigPath = join(
        configDir,
        ".artifact-contracts-analyzer.config.yaml",
      );
      await writeFile(
        tempConfigPath,
        `input:\n  files:\n    - ${yamlBasename}\n`,
      );
      loadConfigPath = tempConfigPath;
    }

    const loadResult = loadDocument(loadConfigPath);
    const resolved = resolveDocument(loadResult);

    const artifactPatterns = new Map<string, string[]>();
    for (const [id, artifact] of Object.entries(resolved.artifacts)) {
      const patterns = readPathPatterns(artifact);
      if (patterns.length > 0) {
        artifactPatterns.set(id, patterns);
      }
    }

    return {
      artifactPatterns,
      artifactDefinitions: resolved.artifacts,
      traceLinks: resolved.trace?.links ?? [],
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    console.warn(
      `[warn] Failed to load artifact-contracts.yaml: ${message}; falling back to agent-contracts`,
    );
    return null;
  } finally {
    process.chdir(previousCwd);
    if (tempConfigPath) {
      await unlink(tempConfigPath).catch(() => {});
    }
  }
}

/**
 * Resolve project artifact patterns. Prefers artifact-contracts.yaml when
 * present; otherwise resolves the agent-contracts DSL. Best-effort: if neither
 * source resolves, returns an empty artifact mapping so the structural pipeline
 * still runs.
 */
export async function loadProjectContext(
  projectRoot: string,
  dslPath?: string,
  artifactContractsPath?: string,
): Promise<ProjectContext> {
  const artifactContracts = await tryLoadArtifactContracts(
    projectRoot,
    artifactContractsPath,
  );
  if (artifactContracts) {
    return {
      artifactPatterns: artifactContracts.artifactPatterns,
      artifactDefinitions: artifactContracts.artifactDefinitions,
      traceLinks: artifactContracts.traceLinks,
    };
  }

  let data: Record<string, unknown> | undefined;
  try {
    const result = await resolveContracts(dslPath ?? projectRoot);
    data = result.data;
  } catch {
    return { artifactPatterns: new Map(), navigationIndex: undefined };
  }

  let navigationIndex: Record<string, unknown> | undefined;
  try {
    navigationIndex = buildNavigationIndex(
      data as Parameters<typeof buildNavigationIndex>[0],
    ) as unknown as Record<string, unknown>;
  } catch {
    navigationIndex = undefined;
  }

  const artifactPatterns = extractArtifactPatterns(navigationIndex ?? data);
  return { artifactPatterns, navigationIndex };
}

/** Return the path to a root `package.json` manifest, if one exists. */
async function findManifests(projectRoot: string): Promise<string[]> {
  const manifest = join(projectRoot, "package.json");
  try {
    const info = await stat(manifest);
    return info.isFile() ? [manifest] : [];
  } catch {
    return [];
  }
}

export interface BuildGraphResult {
  graph: DependencyGraph;
  navigationIndex?: Record<string, unknown>;
  store?: StoreHandle;
  /** File contents loaded during AST build (avoids re-reading in context-pack). */
  fileContents: Map<string, string>;
  unresolvedEdges: UnresolvedEdge[];
  providerErrors: ProviderError[];
}

/**
 * Run the AST → Dependency Graph stages for a project. Opens the persistent
 * store when caching is enabled; the caller owns closing the returned store.
 */
export async function buildProjectGraph(options: {
  projectRoot: string;
  dslPath?: string;
  artifactContractsPath?: string;
  useCache: boolean;
  externalProviders?: InsightProvider[];
  changedFiles?: string[];
  changedSymbols?: string[];
}): Promise<BuildGraphResult> {
  const {
    projectRoot,
    dslPath,
    artifactContractsPath,
    useCache,
    externalProviders,
    changedFiles,
    changedSymbols,
  } = options;

  const t0 = performance.now();
  const { artifactPatterns, navigationIndex } = await loadProjectContext(
    projectRoot,
    dslPath,
    artifactContractsPath,
  );
  const t1 = performance.now();

  const store = useCache ? await openStore() : undefined;
  const registry = createDefaultRegistry();

  const { graph: ast, fileContents } = await buildAstGraph({
    projectRoot,
    artifactPatterns,
    registry,
    store,
  });
  const t2 = performance.now();

  const manifests = await findManifests(projectRoot);
  const graph = await buildDependencyGraph({
    ast,
    projectRoot,
    artifactPatterns,
    manifests,
  });

  if (store) {
    const candidateEdges = loadCandidateEdges(store);
    if (candidateEdges.length > 0) {
      graph.edges.push(...candidateEdges);
    }
  }

  let unresolvedEdges: UnresolvedEdge[] = [];
  let providerErrors: ProviderError[] = [];

  if (externalProviders && externalProviders.length > 0) {
    const tExt0 = performance.now();
    const integration = await integrateExternalInsights(
      graph,
      externalProviders,
      {
        projectRoot,
        changedFiles,
        changedSymbols,
      },
    );
    graph.edges.splice(0, graph.edges.length, ...integration.graph.edges);
    unresolvedEdges = integration.unresolvedEdges;
    providerErrors = integration.providerErrors;

    for (const err of providerErrors) {
      console.error(
        `[external] provider error (${err.providerName}): ${err.message}`,
      );
    }
    if (unresolvedEdges.length > 0) {
      console.error(
        `[external] ${unresolvedEdges.length} unresolved edge(s)`,
      );
      for (const edge of unresolvedEdges) {
        console.error(
          `[external] unresolved: ${edge.from} -> ${edge.to} (${edge.reason})`,
        );
      }
    }

    const tExt1 = performance.now();
    console.error(
      `[perf] integrateExternalInsights: ${(tExt1 - tExt0).toFixed(0)}ms`,
    );
  }

  const t3 = performance.now();

  console.error(`[perf] loadProjectContext: ${(t1 - t0).toFixed(0)}ms`);
  console.error(`[perf] buildAstGraph: ${(t2 - t1).toFixed(0)}ms`);
  console.error(`[perf] buildDependencyGraph: ${(t3 - t2).toFixed(0)}ms`);
  console.error(`[perf] buildProjectGraph total: ${(t3 - t0).toFixed(0)}ms`);

  return {
    graph,
    navigationIndex,
    store,
    fileContents,
    unresolvedEdges,
    providerErrors,
  };
}
